import { Box, useMediaQuery } from '@mui/material';
import { MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { HostPayment } from '../../../app/Slices/SevenDayApiTypes';
import { formatCurrencyShort, getPaymentStatus } from '../../../shared/utils';
import GenericTable, {
    LinkType,
    tableBodyCellsStyles,
    tableHeadCellStyles,
} from '../../shared/GenericTable';
export const PaymentsList = ({ payments }: { payments: HostPayment[] }) => {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const [searchParams, _] = useSearchParams();
    const projected = searchParams.get('projected') === 'true';

    const columns: MRT_ColumnDef<any>[] = [
        {
            accessorKey: 'period',
            header: 'Period',
        },
        {
            accessorKey: 'ref',
            header: 'Ref.',
            size: 100,
        },
        {
            accessorKey: 'bookingsTotal',
            header: 'Reservation Total',
            filterFn: 'betweenInclusive',
            Cell: ({ cell }) => formatCurrencyShort(cell.getValue<number>()),
            mantineTableHeadCellProps: {
                align: 'right',
                style: { ...tableHeadCellStyles, paddingRight: 40 },
            },
            mantineTableBodyCellProps: {
                align: 'right',
                style: { ...tableBodyCellsStyles, paddingRight: 40 },
            },
        },

        {
            accessorKey: 'serviceFeeAmount',
            header: '7DAY Service Fee',
            filterFn: 'betweenInclusive',
            Cell: ({ cell }) => formatCurrencyShort(cell.getValue<number>()),
            mantineTableHeadCellProps: {
                align: 'right',
                style: { ...tableHeadCellStyles, paddingRight: 40 },
            },
            mantineTableBodyCellProps: {
                align: 'right',
                style: { ...tableBodyCellsStyles, paddingRight: 40 },
            },
        },
        {
            accessorKey: 'expensesTotal',
            header: 'Expense Totals',
            size: 100,
            filterFn: 'betweenInclusive',
            Cell: ({ cell }) => formatCurrencyShort(cell.getValue<number>()),
            mantineTableHeadCellProps: {
                align: 'right',
                style: { ...tableHeadCellStyles, paddingRight: 40 },
            },
            mantineTableBodyCellProps: {
                align: 'right',
                style: { ...tableBodyCellsStyles, paddingRight: 40 },
            },
        },
        {
            accessorKey: 'refundTotal',
            header: 'Refund Total',
            size: 100,
            filterFn: 'betweenInclusive',
            Cell: ({ cell }) => formatCurrencyShort(cell.getValue<number>()),
            mantineTableHeadCellProps: {
                align: 'right',
                style: { ...tableHeadCellStyles, paddingRight: 40 },
            },
            mantineTableBodyCellProps: {
                align: 'right',
                style: { ...tableBodyCellsStyles, paddingRight: 40 },
            },
        },

        {
            accessorKey: 'amount',
            header: 'Gross',
            filterFn: 'betweenInclusive',
            Cell: ({ cell }) => formatCurrencyShort(cell.getValue<number>()),
            mantineTableHeadCellProps: {
                align: 'right',
                style: { ...tableHeadCellStyles, paddingRight: 40 },
            },
            mantineTableBodyCellProps: {
                align: 'right',
                style: { ...tableBodyCellsStyles, paddingRight: 40 },
            },
        },
        {
            accessorKey: 'paidAt',
            header: 'Paid Date',
            size: 100,
        },
        {
            accessorKey: 'status',
            header: 'Status',
            size: 100,
            filterVariant: 'select',
            filterFn: (row, columnId, filterValue) => {
                if (!filterValue) return true;
                const value = row.getValue(columnId);
                return value === filterValue;
            },
            mantineFilterSelectProps: {
                data: [
                    { label: 'Paid', value: 'Paid' },
                    { label: 'Unpaid', value: 'Unpaid' },
                ],
                clearable: true,
            },
        },
    ];

    const rows = payments.map((payment) => {
        const {
            id,
            periodStart,
            periodEnd,
            bookingsTotal,
            serviceFeeAmount,
            expensesTotal,
            refundTotal,
            amount,
            amountDue,
            paidAt,
            year,
            month,
            ownerId,
        } = payment;
        return {
            id: id ? id : 0,
            ref: id ? `#${id}` : '',
            period: `${moment(new Date(periodStart)).format(
                'D MMM',
            )} - ${moment(new Date(periodEnd)).format('D MMM, YYYY')}`,
            bookingsTotal,
            serviceFeeAmount,
            expensesTotal,
            refundTotal,
            amount,
            paidAt: paidAt ? moment(paidAt).format('D MMM YYYY') : '',
            status: getPaymentStatus(payment),
            year,
            month,
            ownerId,
        };
    });

    const processedData = useMemo(() => {
        if (!rows) return [];
        return rows;
    }, [rows]);

    return (
        <Box
            sx={{
                height: 'calc(100vh - var(--app-prompt-banner-height, 0px))',
                width: isMobile ? '90vw' : '100%',
                paddingTop: '50px',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    overflowX: 'auto',
                }}
            >
                <GenericTable
                    columns={columns}
                    data={processedData}
                    tableName='7DAY-Earnings-Report'
                    loading={!rows}
                    linkType={LinkType.Bill}
                    projectedEarnings={projected}
                />
            </Box>
        </Box>
    );
};
